import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/** À l'écran une colonne s'écrit « @Nom » ; le serveur lit « {id} ».
 *  Les noms les plus longs d'abord, pour que « @Type de clé » gagne sur « @Type ». */
export function versEcran(template: string, colonnes: { id: string; name: string }[]): string {
  return (template || '').replace(/\{([a-zA-Z0-9_]+)\}/g, (m, id) => { const c = colonnes.find((x) => x.id === id); return c ? `@${c.name}` : m })
}
export function versServeur(texte: string, colonnes: { id: string; name: string }[]): string {
  let out = texte || ''
  for (const c of [...colonnes].sort((a, b) => b.name.length - a.name.length)) {
    out = out.split(`@${c.name}`).join(`{${c.id}}`)
  }
  return out
}

/** Zone de texte avec sélecteur « @ » : on tape @, les colonnes apparaissent,
 *  on filtre, flèches + Entrée (ou clic) pour insérer {id}. Comme dans un CRM. */
export function ZoneVariables({ value, onChange, colonnes, placeholder, className, bulle, hint }: {
  value: string; onChange: (v: string) => void; colonnes: { id: string; name: string }[]; placeholder?: string; className?: string; bulle?: 'voix' | 'texto'; hint?: string
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLTextAreaElement>(null)
  const [menu, setMenu] = useState<{ debut: number; requete: string } | null>(null)
  const [actif, setActif] = useState(0)
  const propositions = menu ? colonnes.filter((c) => c.name.toLowerCase().includes(menu.requete.toLowerCase()) || c.id.includes(menu.requete.toLowerCase())) : []

  const detecter = (texte: string, curseur: number) => {
    const avant = texte.slice(0, curseur)
    let m = /(?:^|[\s(:,.])@([^\s@{}]*)$/.exec(avant)
    // Un « @ » collé à une pastille (« @Marque@ ») compte aussi.
    if (!m) {
      const m2 = /@([^\s@{}]*)$/.exec(avant)
      const tete = m2 ? avant.slice(0, avant.length - m2[0].length) : ''
      if (m2 && colonnes.some((c) => tete.endsWith(`@${c.name}`))) m = m2
    }
    // Un « @Nom » déjà complet n'ouvre pas le menu.
    if (m && colonnes.some((c) => c.name === m[1])) { setMenu(null); return }
    if (m) { setMenu({ debut: curseur - m[1].length - 1, requete: m[1] }); setActif(0) } else setMenu(null)
  }
  const choisir = (c: { id: string; name: string }) => {
    if (!menu) return
    const el = ref.current
    const fin = el ? el.selectionStart : value.length
    const apres = value.slice(fin)
    // Espace avant si collé à une pastille, espace après toujours : on enchaîne @…@… sans y penser.
    const avant = value.slice(0, menu.debut)
    const sep = avant && !/\s$/.test(avant) ? ' ' : ''
    const jeton = `${sep}@${c.name}${/^\s/.test(apres) ? '' : ' '}`
    const nouveau = `${avant}${jeton}${apres}`
    const pos = menu.debut + jeton.length
    onChange(nouveau); setMenu(null)
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = pos } })
  }
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menu || !propositions.length) { if (e.key === 'Escape') setMenu(null); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActif((a) => (a + 1) % propositions.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((a) => (a - 1 + propositions.length) % propositions.length) }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choisir(propositions[actif]) }
    else if (e.key === 'Escape') { e.preventDefault(); setMenu(null) }
  }
  // Calque derrière la zone : même police, mêmes marges ; le texte du textarea est
  // transparent (seul le curseur reste visible), le calque montre les pastilles.
  const calque = useRef<HTMLDivElement>(null)
  const noms = [...colonnes].sort((a, b) => b.name.length - a.name.length).map((c) => c.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const motif = noms.length ? new RegExp(`(@(?:${noms.join('|')}))`, 'g') : null
  const morceaux = motif ? value.split(motif) : [value]
  const sync = () => { if (calque.current && ref.current) calque.current.scrollTop = ref.current.scrollTop }
  // La zone grandit avec le contenu : jamais d'ascenseur, le calque suit.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }, [value])
  const police = 'px-3 py-2 text-sm leading-6 whitespace-pre-wrap break-words'
  // Bulle de parole (coin haut gauche pointu) ou bulle de texto (coin bas droit pointu).
  const forme = bulle === 'voix' ? 'rounded-2xl rounded-tl-md' : bulle === 'texto' ? 'rounded-2xl rounded-br-md' : 'rounded-lg'
  return (
    <div className={cn('relative', forme)}>
      <div ref={calque} aria-hidden className={cn('absolute inset-0 overflow-hidden pointer-events-none border border-transparent text-slate-900 dark:text-slate-100', police, forme)}>
        {morceaux.map((m, k) => k % 2 === 1
          ? <span key={k} className="rounded bg-blue-100 dark:bg-blue-500/25 text-blue-800 dark:text-blue-200">{m}</span>
          : <Fragment key={k}>{m}</Fragment>)}
        {'\n'}
      </div>
      <textarea ref={ref} className={cn('relative w-full border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400',
          'bg-transparent border-slate-300 dark:border-slate-700',
          'text-transparent caret-slate-900 dark:caret-slate-100 selection:bg-blue-200/60 resize-none overflow-hidden', police, forme, className)} value={value} placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); detecter(e.target.value, e.target.selectionStart) }} onScroll={sync}
        onKeyDown={onKeyDown} onClick={(e) => detecter(value, e.currentTarget.selectionStart)} onBlur={() => setTimeout(() => setMenu(null), 150)} />
      {menu && propositions.length > 0 && (
        <div className="absolute left-3 z-20 mt-1 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1 overflow-hidden">
          {propositions.map((c, k) => (
            <button key={c.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choisir(c)} onMouseEnter={() => setActif(k)}
              className={cn('w-full text-left px-3 py-1.5 text-sm flex items-center justify-between', k === actif ? 'bg-blue-500 text-white' : 'text-slate-800 dark:text-slate-100')}>
              <span>{c.name}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-1.5">{t(hint ?? 'manager.lookup.atHint', 'Tapez @ pour insérer une colonne.')}</p>
    </div>
  )
}

